import {
  ActionsButton,
  ActionTypes,
  ButtonCallbacks,
  DataTableHeader,
  TableStatusChip,
  DataTable
} from '@/components/types/DataTable';
import {
  MANAGED_DEVICE_ADMIN_STATUS_MANAGED,
  MANAGED_DEVICE_CONNECTIVITY_STATUS_ONLINE,
  MANAGED_DEVICE_CONNECTIVITY_STATUS_OFFLINE,
  MANAGED_DEVICE_ADMIN_STATUS_COMMISSIONING,
  MANAGED_DEVICE_ADMIN_STATUS_SERVICE,
  SMART_UPDATE_PROGRESS_SUCCESS
} from '@/models/inventory/constants';
import { Device } from '@/store/types';
import { SmartUpdateStatus } from '../../store/types/inventory/Device';
import { colors } from '@/styles/colors';
import { Progress } from '@/store/types/rollouts/Deployment';

export const deviceActionsButtonConfig: ActionsButton[] = [
  {
    id: ButtonCallbacks.ADD,
    icon: 'mdi-plus',
    title: 'common.add',
    types: [ActionTypes.NONE],
    primary: true
  },
  {
    id: ButtonCallbacks.UPDATE_FIRMWARE,
    icon: 'mdi-update',
    title: 'devices.actionButtonActions.updateFirmware',
    types: [ActionTypes.SINGLE, ActionTypes.MULTIPLE]
  },
  {
    id: ButtonCallbacks.UPDATE_PACKAGE,
    icon: 'mdi-package-variant-closed-check',
    title: 'devices.actionButtonActions.updatePackage',
    types: [ActionTypes.SINGLE, ActionTypes.MULTIPLE],
    multiSelectionDialogConfig: {
      visible: false
    }
  },
  {
    id: ButtonCallbacks.UPDATE_TEMPLATE,
    icon: 'mdi-code-block-tags',
    title: 'devices.actionButtonActions.updateTemplate',
    types: [ActionTypes.SINGLE, ActionTypes.MULTIPLE],
    multiSelectionDialogConfig: {
      visible: false
    }
  },
  {
    id: ButtonCallbacks.NONE,
    title: 'common.empty',
    types: [ActionTypes.SINGLE, ActionTypes.MULTIPLE]
  },
  {
    id: ButtonCallbacks.LOGS,
    icon: 'mdi-note-text',
    title: 'devices.showEventLog',
    types: [ActionTypes.SINGLE]
  },
  {
    id: ButtonCallbacks.DOWNLOAD,
    icon: 'mdi-download',
    title: 'devices.downloadStartConfig',
    types: [ActionTypes.SINGLE]
  },
  {
    id: ButtonCallbacks.NONE,
    title: 'common.empty',
    types: [ActionTypes.SINGLE, ActionTypes.MULTIPLE]
  },
  {
    id: ButtonCallbacks.EDIT,
    icon: 'mdi-pencil',
    title: 'common.edit',
    types: [ActionTypes.SINGLE]
  },
  {
    id: ButtonCallbacks.TOGGLE,
    icon: 'mdi-autorenew',
    title: 'devices.actionButtonActions.automaticFirmwareAutoUpdate',
    types: [ActionTypes.SINGLE, ActionTypes.MULTIPLE],
    multiSelectionDialogConfig: {
      visible: false
    }
  },
  {
    id: ButtonCallbacks.MOVE_ROUTERS,
    icon: 'mdi-tag-plus',
    title: 'devices.actionButtonActions.addToGroup',
    types: [ActionTypes.MULTIPLE, ActionTypes.SINGLE],
    multiSelectionDialogConfig: {
      positionIndex: 1
    }
  },
  {
    id: ButtonCallbacks.REMOVE,
    icon: 'mdi-delete',
    title: 'common.delete',
    types: [ActionTypes.SINGLE, ActionTypes.MULTIPLE],
    multiSelectionDialogConfig: {
      positionIndex: 0,
      color: colors.danger
    }
  },
  // Three Dot Menu
  {
    id: ButtonCallbacks.UPLOAD,
    icon: 'mdi-cloud-upload',
    title: 'devices.bulkImport',
    types: [ActionTypes.NONE]
  },
  {
    id: ButtonCallbacks.REPORT,
    icon: 'mdi-file-chart-outline',
    title: 'report.generate',
    types: [ActionTypes.NONE]
  },
  {
    id: ButtonCallbacks.NONE,
    title: 'divider',
    types: [ActionTypes.NONE]
  },
  {
    id: ButtonCallbacks.REFRESH,
    icon: 'mdi-refresh',
    title: 'common.refresh',
    types: [ActionTypes.NONE]
  },
  {
    id: ButtonCallbacks.EDIT_TABLE,
    icon: 'mdi-table-edit',
    title: 'devices.actionButtonActions.tableSettings',
    types: [ActionTypes.NONE]
  }
];

export const deviceTableConfig: DataTable = {
  tableId: 'deviceTable',
  persistentFilter: true,
  toolbarConfig: {
    titleCounterConfig: { title: 'common.devices' },
    iconButtonConfig: []
  },
  noDataConfig: {
    actionButtonText: 'devices.noDataButtonText',
    alertMessage: 'devices.noDataAlertText',
    icon: 'mdi-router-wireless',
    callback: undefined
  }
};

export const deviceStatusConfig: TableStatusChip[] = [
  {
    statusString: MANAGED_DEVICE_ADMIN_STATUS_MANAGED,
    translationKey: 'common.statusChip.managed',
    color: colors.primary
  },
  {
    statusString: MANAGED_DEVICE_ADMIN_STATUS_COMMISSIONING,
    translationKey: 'common.statusChip.commissioning',
    color: colors.mittelBlau20
  },
  {
    statusString: MANAGED_DEVICE_ADMIN_STATUS_SERVICE,
    translationKey: 'common.statusChip.service',
    color: colors.warning
  },
  {
    statusString: MANAGED_DEVICE_CONNECTIVITY_STATUS_ONLINE,
    translationKey: 'common.statusChip.online',
    color: colors.success
  },
  {
    statusString: MANAGED_DEVICE_CONNECTIVITY_STATUS_OFFLINE,
    translationKey: 'common.statusChip.offline',
    color: colors.hellOrange
  }
];

export const smartUpdateStatusConfig: TableStatusChip[] = [
  {
    statusString: SMART_UPDATE_PROGRESS_SUCCESS,
    translationKey: 'common.statusChip.success',
    color: colors.success
  },
  {
    statusString: SmartUpdateStatus.FAILED,
    translationKey: 'common.statusChip.failed',
    color: colors.danger
  },
  {
    statusString: SmartUpdateStatus.PENDING,
    translationKey: 'common.statusChip.pending',
    color: colors.secondary
  },
  {
    statusString: SmartUpdateStatus.LATEST,
    translationKey: 'common.statusChip.latest',
    color: colors.success
  },
  {
    statusString: SmartUpdateStatus.SCHEDULED,
    translationKey: 'common.statusChip.scheduled',
    color: colors.darkSecondary
  },
  // Timeout is an UpdateJobState; needed to display error tatus of smart update in table
  {
    statusString: Progress.TIMEOUT,
    translationKey: 'common.statusChip.failed',
    color: colors.danger
  },
  {
    statusString: SmartUpdateStatus.UNKNOWN,

    translationKey: 'common.statusChip.unknown',
    color: colors.mediumShadow
  }
];

export const deviceTableConfigurableColumns: DataTableHeader<Device>[] = [
  {
    title: 'devices.name',
    value: 'name',
    default: true
  },
  {
    title: 'common.routerModel',
    value: 'hardwareModel',
    default: true
  },
  {
    title: 'devices.firmware',
    value: 'firmwareVersion',
    default: true
  },
  {
    title: 'devices.deviceGroup',
    value: 'groupName',
    default: true
  },
  {
    title: 'devices.location',
    value: 'location',
    default: true
  },
  {
    title: 'devices.firmwareAutoUpdate',
    value: 'firmwareAutoUpdate',
    default: true,
    excludedInOnPrem: true
  },
  {
    title: 'devices.clientIP',
    value: 'url',
    default: true,
    excludedInCloud: true
  },
  {
    title: 'common.status',
    value: 'connectionStatus',
    default: true
  },
  {
    title: 'common.createdAt',
    value: 'createdAt'
  },
  {
    title: 'common.updatedAt',
    value: 'updatedAt'
  },
  {
    title: 'common.serialNumber',
    value: 'serialNumber'
  },
  {
    title: 'devices.connectionLastMessageAt',
    value: 'connectionLastMessageAt'
  },
  {
    title: 'devices.connectionStartedAt',
    value: 'connectionStartedAt'
  }
];

export const deviceThreeDotMenuPatch = {
  id: ButtonCallbacks.TOGGLE,
  title: 'devices.actionButtonActions.threeDotMenuSmartUpdate'
};
